import axios from "axios";
import { storageHelper } from '../../Helpers/storageHelper';
import { AppKeys } from "../Shared/AppKeys";
import { decryptData } from '../../utils/encryption';

const sanitizeHeaderValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  return String(value).replace(/[\u0000-\u001f\u007f]/g, "").trim();
};

const getUrlAuthToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  return sanitizeHeaderValue(searchParams.get("Auth") || searchParams.get("token"));
};

const applyAuthHeader = (config = {}, authToken = null) => {
  const { headers: incomingHeaders = {}, authToken: _authToken, ...restConfig } = config;
  const headers = { ...incomingHeaders };
  const explicitToken = sanitizeHeaderValue(authToken);

  if (explicitToken) {
    headers.Authorization = explicitToken;
  }

  return {
    ...restConfig,
    headers,
  };
};

/**
 * Axios instance configuration with default settings
 * - Base URL: Url of the API (Production or Development)
 * - Timeout: 10 seconds
 * - Default headers: JSON content type, JSON accept, Arabic language
 */
const axiosInstance = axios.create({
   baseURL: AppKeys._API_PRODUCTION_URL,
    timeout: 200000,
    headers: {
      "Accept": "*/*",
      "Accept-Language": "ar",
    },
});

/**
 * Retrieves the currently selected language from localStorage,
 * which is managed by LanguageContext.jsx
 */
const getSelectedLanguage = () => {
  if (typeof window === "undefined") {
    return "ar";
  }
  try {
    return localStorage.getItem("appLanguage") || "ar";
  } catch (e) {
    return "ar";
  }
};

/**
 * Request Interceptor
 * - Adds authentication token to all requests if available
 * - Token is retrieved from localStorage
 * - Sets Accept-Language header based on selected language
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const headers = config.headers || {};
    
    // Dynamically set Accept-Language based on user selection in LanguageContext.jsx
    const selectedLang = getSelectedLanguage();
    if (!headers["Accept-Language"] && !headers["accept-language"]) {
      headers["Accept-Language"] = selectedLang;
    } else if (headers["Accept-Language"] === "ar") {
      headers["Accept-Language"] = selectedLang;
    } else if (headers["accept-language"] === "ar") {
      headers["accept-language"] = selectedLang;
    }

    const currentToken = sanitizeHeaderValue(headers.Authorization || headers.authorization);

    if (!currentToken) {
      const token = sanitizeHeaderValue(storageHelper.getItem("token")) || getUrlAuthToken();
      if (token) {
        config.headers = {
          ...headers,
          Authorization: token,
        };
      } else {
        config.headers = headers;
      }
      return config;
    }

    config.headers = {
      ...headers,
      Authorization: currentToken,
    };

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor
 * - Handles global errors
 * - Passes through successful responses
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    
    if (error.response?.status === 401) {
      storageHelper.removeItem("token"); // Remove token
      storageHelper.removeItem("data");
      window.location.href = "/signin"; // Redirect to login
      return Promise.reject(error);
    }
    
    if (error.response?.status === 403) {
      // Store the error message in localStorage to access it in the Forbidden component
      storageHelper.setItem('forbiddenMessage', error.response.data.message || 'غير مصرح بالوصول');
      // Redirect to the forbidden page
      window.location.href = '/forbidden';
      return Promise.reject(error);
    }
    
    if (error.response?.status === 429) {
      // Handle rate limiting
      const retryAfter = error.response.headers['retry-after'] || error.response.headers['Retry-After'];
      const rateLimitMessage = error.response.data?.message || error.response.data?.msg || 'تم تجاوز حد الطلبات المسموح. يرجى المحاولة مرة أخرى لاحقاً.';
      
      // Store rate limit information
      storageHelper.setItem('rateLimitMessage', rateLimitMessage);
      if (retryAfter) {
        storageHelper.setItem('retryAfter', retryAfter);
      }
      
      // Show user-friendly message (you can customize this based on your UI needs)
      
      if (retryAfter) {
        
      }
      
      // You can optionally redirect to a rate limit page or show a modal
      // window.location.href = '/rate-limit';
      
      return Promise.reject(error);
    }
    
    if (error.response?.status === 503) {
      // Store the maintenance message from server response
      const maintenanceMessage = error.response.data?.message || error.response.data?.msg || 'نحن في صيانة، سيتم إعادة الخدمة قريباً';
      storageHelper.setItem('maintenanceMessage', maintenanceMessage);
      // Redirect to maintenance mode page
      setTimeout(() => {
        window.location.href = '/maintenance';
      }, 100);
      
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

/**
 * API Service Methods
 * Collection of HTTP methods for making API requests
 */
const ApiServices = {
  /**
   * Performs a GET request
   * @param {string} url - The endpoint URL
   * @param {Object} params - Query parameters (optional)
   * @param {number} timeout - Request timeout in milliseconds (default: 200000)
   * @param {Object} config - Additional axios configuration (optional)
   * @returns {Promise} Response data
   * @throws {Error} API error response
   */
  get: async (url, params = {}, timeout = 200000, config = {}) => {
    try {
      const mergedConfig = applyAuthHeader({ params, timeout, ...config }, config.authToken);
      const response = await axiosInstance.get(url, mergedConfig);
      if (typeof response.data === "string" && !config.skipDecrypt && !config.isRawResponse) {
        const decryptedData = decryptData(response.data);
        if (decryptedData !== null) {
          return decryptedData;
        }
        // If string is raw unencrypted data (e.g., base64 image data URI), return response.data directly
        return response.data;
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Performs a POST request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {boolean} isFormData - Whether the request contains form data
   * @param {number} timeout - Request timeout in milliseconds (default: 200000)
   * @param {Object} config - Additional axios configuration
   * @returns {Promise} Response data
   * @throws {Error} API error response
   */
  post: async (url, data, isFormData = false, timeout = 200000, config = {}) => {
    try {
      let requestConfig = applyAuthHeader({
        timeout: timeout,
        ...config
      }, config.authToken);
      
      if (isFormData) {
        requestConfig.headers = {
          ...requestConfig.headers,
          "Accept": "*/*",
          "Content-Type": "multipart/form-data",
        };
      }
      
      
      
      
      
      
      
      
      const response = await axiosInstance.post(url, data, requestConfig);
      return response.data;
    } catch (error) {
      
      
      
      
      
      
      
      
      
      throw error.response?.data || error;
    }
  },

  /**
   * Performs a PUT request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {boolean} isFormData - Whether the request contains form data
   * @param {number} timeout - Request timeout in milliseconds (default: 200000)
   * @returns {Promise} Response data
   * @throws {Error} API error response
   */
  put: async (url, data, isFormData = false, timeout = 200000, config = {}) => {
    try {
      let requestConfig = applyAuthHeader({
        timeout: timeout
      }, config.authToken);
      
      if (isFormData) {
        // For FormData requests, don't set Content-Type header
        // Let the browser set it with the correct boundary
        requestConfig.headers = {
          ...requestConfig.headers,
          "Accept": "*/*",
          "Content-Type": "multipart/form-data",
        };
      }
      
      const response = await axiosInstance.put(url, data, requestConfig);
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },

  /**
   * Performs a PATCH request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @returns {Promise} Response data
   * @throws {Error} API error response
   */
  patch: async (url, data, config = {}) => {
    try {
      const response = await axiosInstance.patch(url, data, applyAuthHeader(config, config.authToken));
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },

  /**
   * Performs a DELETE request without body
   * @param {string} url - The endpoint URL
   * @returns {Promise} Response data
   * @throws {Error} API error response
   */
  delete: async (url, config = {}) => {
    try {
      const response = await axiosInstance.delete(url, applyAuthHeader(config, config.authToken));
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },

  /**
   * Performs a DELETE request with body data
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @returns {Promise} Response data
   * @throws {Error} API error response
   */
  deleteWithData: async (url, data, config = {}) => {
    try {
      const response = await axiosInstance.delete(url, {
        data: data,
        ...applyAuthHeader(config, config.authToken)
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },

  /**
   * Downloads a file from the given endpoint and triggers a browser download
   * @param {string} url - The endpoint URL
   * @param {number} timeout - Request timeout in milliseconds (default: 200000)
   * @param {Object} config - Additional axios configuration (e.g., onDownloadProgress)
   */
  downloadFile: async (url, timeout = 200000, config = {}) => {
    try {
      const mergedConfig = applyAuthHeader({
        responseType: 'blob',
        headers: {
          'Accept': '*/*',
          'Accept-Language': 'en',
        },
        timeout,
        ...config
      }, config.authToken);
      const response = await axiosInstance.get(url, mergedConfig);
      const blob = new Blob([response.data], { type: 'application/vnd.android.package-archive' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'emthany-app.apk';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default ApiServices;
